import { Injectable } from '@angular/core';
import { CookieConfig } from '../../../app/models/auth.models';
import { LoggerService } from '../../logger-services/logger.service';

@Injectable({
  providedIn: 'root',
})
export class DevCookieService {
  private readonly DevCookieServiceError = (error: any) =>
    `[DevCookieService] - ${JSON.stringify(error)}`;

  constructor(private readonly loggerService: LoggerService) {}
  /**
   * Set a cookie with the given configuration
   */
  setCookie(config: CookieConfig): void {
    let cookieString = `${config.name}=${config.value}`;

    if (config.domain) {
      cookieString += `; Domain=${config.domain}`;
    }

    if (config.path) {
      cookieString += `; Path=${config.path}`;
    } else {
      cookieString += `; Path=/`;
    }

    if (config.expires) {
      cookieString += `; Expires=${config.expires.toUTCString()}`;
    }

    if (config.maxAge !== undefined) {
      cookieString += `; Max-Age=${config.maxAge}`;
    }

    if (config.secure) {
      cookieString += `; Secure`;
    }

    if (config.httpOnly) {
      cookieString += `; HttpOnly`;
    }

    if (config.sameSite) {
      cookieString += `; SameSite=${config.sameSite}`;
    }

    try {
      document.cookie = cookieString;
    } catch (error: any) {
      this.loggerService.logError(this.DevCookieServiceError(error), "setCookie");
    }
  }

  /**
   * Get a cookie value by name
   */
  getCookie(name: string): string | null {
    try {
      const nameEQ = name + '=';
      const ca = document.cookie.split(';');

      for (const cookie of ca) {
        let c = cookie;
        while (c.charAt(0) === ' ') {
          c = c.substring(1, c.length);
        }
        if (c.indexOf(nameEQ) === 0) {
          return c.substring(nameEQ.length, c.length);
        }
      }
      return null;
    } catch (error: any) {
      this.loggerService.logError(this.DevCookieServiceError(error), "getCookie");
      return null;
    }
  }

  /**
   * Delete a cookie by name
   */
  deleteCookie(name: string, domain?: string, path?: string): void {
    try {
      let cookieString = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC`;

      if (domain) {
        cookieString += `; Domain=${domain}`;
      }

      if (path) {
        cookieString += `; Path=${path}`;
      } else {
        cookieString += `; Path=/`;
      }

      document.cookie = cookieString;
    } catch (error: any) {
      this.loggerService.logError(this.DevCookieServiceError(error), "deleteCookie");
    }
  }

  /**
   * Check if a cookie exists
   */
  hasCookie(name: string): boolean {
    return this.getCookie(name) !== null;
  }

  /**
   * Clear all cookies (use with caution)
   */
  clearAllCookies(): void {
    const cookies = document.cookie.split(';');

    for (const cookie of cookies) {
      const eqPos = cookie.indexOf('=');
      const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
      this.deleteCookie(name);
    }
  }
}
